/**
 * Shell/deeplink sometimes produces "#/card-shield%3Fview%3Dall?redirectUrl=..." so the encoded
 * `?` sits in the path segment; HashRouter then pathname !== "/card-shield" and Card Shield never mounts.
 * Decode path + merge params, drop redundant hash `redirectUrl`, then replaceState before React boots.
 *
 * @returns `true` if `history.replaceState` ran with a corrected hash; `false` if nothing changed or on error.
 *
 * @see docs/guides/hash-decode-normalization-before-app-boot.md — case matrix and scope vs in-app hash handling.
 */
export function normalizeHashEncodedQueryBeforeBoot(): boolean {
  try {
    const locationHash = window.location.hash;
    // `#` alone has no routable body; skip so we never emit an empty hash rewrite.
    if (!locationHash || locationHash.length < 2) {
      return false;
    }

    // Everything after `#` is the hash "URL" body (pathname + query as the shell encoded it).
    const hashBodyWithoutOctothorpe = locationHash.slice(1);

    // HashRouter splits on the first *literal* `?`. Malformed URLs put the real query inside the path
    // as `%3F…`, so the "path" segment is wrong until we decode. Split the same way here first.
    const firstLiteralQuestionMarkIndex = hashBodyWithoutOctothorpe.indexOf('?');
    const pathSegmentBeforeLiteralQuestion =
      firstLiteralQuestionMarkIndex >= 0
        ? hashBodyWithoutOctothorpe.slice(0, firstLiteralQuestionMarkIndex)
        : hashBodyWithoutOctothorpe;
    const tailQueryStringAfterLiteralQuestion =
      firstLiteralQuestionMarkIndex >= 0 ? hashBodyWithoutOctothorpe.slice(firstLiteralQuestionMarkIndex + 1) : '';

    // Only fix when percent-encoding appears in that path slice. Encoded values in the tail query
    // (e.g. redirectUrl=%2F…) are normal and must not trigger a rewrite.
    if (!/%[0-9A-Fa-f]{2}/.test(pathSegmentBeforeLiteralQuestion)) {
      return false;
    }

    // Undo one or more layers of encoding (some shells double-encode `%3F` as `%253F`).
    let decodedPathSegment = pathSegmentBeforeLiteralQuestion;
    for (let decodePassIndex = 0; decodePassIndex < 4; decodePassIndex += 1) {
      try {
        const pathSegmentAfterDecodePass = decodeURIComponent(decodedPathSegment);
        if (pathSegmentAfterDecodePass === decodedPathSegment) {
          break;
        }
        decodedPathSegment = pathSegmentAfterDecodePass;
      } catch {
        // Bad `%` sequences: stop decoding; keep last good segment and continue below.
        break;
      }
    }

    // After decode, a real `?` may appear inside the former "path" — that becomes the true route + query.
    const questionMarkIndexInDecodedSegment = decodedPathSegment.indexOf('?');
    const hashRoutePath =
      questionMarkIndexInDecodedSegment >= 0
        ? decodedPathSegment.slice(0, questionMarkIndexInDecodedSegment)
        : decodedPathSegment;
    const queryStringEmbeddedInDecodedPath =
      questionMarkIndexInDecodedSegment >= 0 ? decodedPathSegment.slice(questionMarkIndexInDecodedSegment + 1) : '';

    // Merge: params decoded from the path segment, then tail query (duplicate keys: tail wins).
    const mergedHashSearchParams = new URLSearchParams(queryStringEmbeddedInDecodedPath);
    if (tailQueryStringAfterLiteralQuestion) {
      new URLSearchParams(tailQueryStringAfterLiteralQuestion).forEach(function(value, key) {
        mergedHashSearchParams.set(key, value);
      });
    }

    // Shell often duplicates navigation in `redirectUrl`; drop from the hash after normalization.
    mergedHashSearchParams.delete('redirectUrl');

    const serializedMergedQuery = mergedHashSearchParams.toString();
    const rebuiltHashBodyWithoutOctothorpe = hashRoutePath + (serializedMergedQuery ? '?' + serializedMergedQuery : '');
    const rebuiltLocationHash = '#' + rebuiltHashBodyWithoutOctothorpe;

    // Avoid replaceState/noise when the computed hash matches input (e.g. rare edge cases).
    if (rebuiltLocationHash === locationHash) {
      return false;
    }

    // Preserve non-hash parts of the document URL; only the `#…` fragment changes.
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search + rebuiltLocationHash
    );
    return true;
  } catch (_e) {
    return false;
  }
}
